import useFetch from "hooks/useFetch";
import SectionTabs from "pages/AdminLayout/SectionTabs";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Icon,
  PageHeader,
  Pagination,
  Select,
  Skeleton,
  styles as ui,
} from "stories/Admin";
import PageModal from "../LocationsPage/PageModal";
import { formatLogTime } from "../TvAiLogPage/aiLog";
import {
  DeathNoticeSubmission,
  formatConfidence,
  prettyJson,
  resultLabel,
  RESULTS,
  resultTone,
  SubmissionPage,
  submissionsUrl,
} from "./janazah";
import styles from "./JanazahAiLogPage.module.scss";
import usePendingActions from "./usePendingActions";

/*
 * Superadmin: «AI-logg (janazah)» — hver janazah-plakat folk har lastet opp fra appene,
 * hva AI-en leste, og hva som skjedde (publisert, fantes fra før, venter, avvist …).
 */

const PAGE_SIZE = 30;

const PLATFORM_LABELS: Record<string, string> = {
  ios: "iPhone",
  android: "Android",
};

const seconds = (ms: number | null) =>
  ms == null
    ? null
    : `${(ms / 1000).toLocaleString("nb-NO", { maximumFractionDigits: 1 })} s`;

/** Felter fra AI-svaret som vises i detaljen (det AI-en leste, ikke hva vi lagret). */
const aiFields = (raw: string | null | undefined): Array<[string, string]> => {
  if (!raw) return [];
  try {
    const json = JSON.parse(raw) as Record<string, unknown>;
    const out: Array<[string, string]> = [];
    const add = (label: string, value: unknown) => {
      if (value === null || value === undefined || value === "") return;
      out.push([
        label,
        typeof value === "string" ? value : JSON.stringify(value),
      ]);
    };
    add("Navn", json.name);
    add("Byrå (nøkkel)", json.agency);
    add("Byrå på plakaten", json.agency_name);
    add("Dødsannonse", json.is_death_notice ? "ja" : "nei");
    add("Muslimsk janazah", json.is_muslim_janazah_notice ? "ja" : "nei");
    add(
      "Mistenkelig",
      json.suspicious
        ? `ja${json.suspicious_reason ? `: ${json.suspicious_reason}` : ""}`
        : "nei",
    );
    add("Født", json.birth_date);
    add("Død", json.death_date);
    add("Minneside", json.memorial_url);
    const ceremonies = Array.isArray(json.ceremonies) ? json.ceremonies : [];
    ceremonies.forEach((c: any) =>
      add(
        c.label || c.type,
        [c.date, c.start_time, c.place, c.address].filter(Boolean).join(" · "),
      ),
    );
    const warnings = Array.isArray(json.warnings) ? json.warnings : [];
    if (warnings.length) add("Usikkert", warnings.join(" "));
    return out;
  } catch {
    return [];
  }
};

const JanazahAiLogPage: React.FC = () => {
  const { execute } = useFetch();
  const executeRef = useRef(execute);
  executeRef.current = execute;

  const [page, setPage] = useState(1);
  const [result, setResult] = useState("");
  const [data, setData] = useState<SubmissionPage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [detail, setDetail] = useState<DeathNoticeSubmission | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setFailed(false);
    executeRef
      .current(submissionsUrl(page, PAGE_SIZE, result), "GET")
      .then((res) => {
        if (cancelled) return;
        if (res) setData(res as SubmissionPage);
        else setFailed(true);
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, result, reloadKey]);

  const openDetail = async (item: DeathNoticeSubmission) => {
    setDetail(item);
    setDetailLoading(true);
    const full = (await executeRef.current(
      `/api/v1/death-notices/admin/submissions/${item.id}`,
      "GET",
    )) as DeathNoticeSubmission | null;
    if (full) setDetail(full);
    setDetailLoading(false);
  };

  const pendingActions = usePendingActions(() => {
    setDetail(null);
    reload();
  });

  const items = data?.items ?? [];
  const pending = (s: DeathNoticeSubmission) =>
    s.noticeId != null && s.noticeStatus === "PENDING_REVIEW";

  const PendingButtons: React.FC<{ s: DeathNoticeSubmission }> = ({ s }) => {
    if (!pending(s) || s.noticeId == null) return null;
    const id = s.noticeId;
    const name = s.noticeName || s.extractedName || `#${id}`;
    const busy = pendingActions.busyId === id;
    return (
      <div className={styles.actions}>
        <Button
          size="sm"
          loading={busy}
          disabled={busy}
          onClick={() => pendingActions.approve(id, name, true)}
          icon={<Icon name="bell" size={14} strokeWidth={2} />}
        >
          Godkjenn og send varsel
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={busy}
          onClick={() => pendingActions.approve(id, name, false)}
        >
          Godkjenn uten varsel
        </Button>
        <Button
          size="sm"
          variant="danger"
          disabled={busy}
          onClick={() => pendingActions.remove(id, name)}
          icon={<Icon name="trash" size={14} strokeWidth={2} />}
        >
          Slett
        </Button>
      </div>
    );
  };

  return (
    <>
      <PageHeader
        title="AI-logg (janazah)"
        subtitle="Janazah-plakater lastet opp fra appene: hva AI-en leste og hva som skjedde. Avviste opplastinger slettes etter 90 dager."
        actions={
          <Button
            variant="secondary"
            icon={<Icon name="swap" size={16} />}
            onClick={reload}
            loading={isLoading}
          >
            Oppdater
          </Button>
        }
      />
      <SectionTabs />

      <div className={styles.filters}>
        <Select
          label="Resultat"
          fieldClassName={styles.resultField}
          value={result}
          onChange={(e) => {
            setResult(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Alle resultater</option>
          {RESULTS.map((r) => (
            <option key={r} value={r}>
              {resultLabel(r)}
            </option>
          ))}
        </Select>
        {data && (
          <span className={ui.help}>
            {data.totalItems.toLocaleString("nb-NO")} opplastinger
          </span>
        )}
      </div>

      <Card>
        {failed && !data ? (
          <Alert tone="error">
            Kunne ikke hente AI-loggen. Prøv å oppdatere.
          </Alert>
        ) : isLoading && !data ? (
          <div className={styles.list}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} height={96} radius={14} />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState icon="moon" title="Ingen opplastinger">
            {result
              ? "Ingen opplastinger med dette resultatet."
              : "Ingen har lastet opp janazah-plakater fra appene ennå."}
          </EmptyState>
        ) : (
          <div className={styles.list}>
            {items.map((s) => (
              <article key={s.id} className={styles.row}>
                <button
                  type="button"
                  className={styles.thumbButton}
                  onClick={() => openDetail(s)}
                  aria-label={`Vis opplasting ${s.id}`}
                >
                  {s.imageUrl ? (
                    <img src={s.imageUrl} alt="" className={styles.thumb} />
                  ) : (
                    <span className={styles.thumbEmpty}>
                      <Icon name="moon" size={18} />
                    </span>
                  )}
                </button>
                <div className={styles.body}>
                  <div className={styles.head}>
                    <b>{s.extractedName || "Uten navn"}</b>
                    <Badge tone={resultTone(s.result)}>
                      {resultLabel(s.result)}
                    </Badge>
                    {s.pushSent && (
                      <Badge tone="mint">
                        <Icon name="bell" size={12} strokeWidth={2.2} />
                        Push sendt
                      </Badge>
                    )}
                  </div>
                  <span className={styles.muted}>
                    {formatLogTime(s.createdAt)}
                    {s.platform &&
                      ` · ${PLATFORM_LABELS[s.platform] ?? s.platform}`}
                    {s.appVersion && ` ${s.appVersion}`}
                    {s.agencyName || s.agencyKey
                      ? ` · ${s.agencyName || s.agencyKey}`
                      : ""}
                    {s.confidence != null &&
                      ` · sikkerhet ${formatConfidence(s.confidence)}`}
                  </span>
                  {s.reason && (
                    <span className={styles.reason}>{s.reason}</span>
                  )}
                  {s.noticeId != null && (
                    <span className={styles.muted}>
                      Annonse #{s.noticeId}
                      {s.noticeName ? `: ${s.noticeName}` : " (slettet)"}
                      {s.noticeStatus === "PENDING_REVIEW" &&
                        " · venter på godkjenning"}
                      {s.noticeStatus === "PUBLISHED" && " · publisert"}
                    </span>
                  )}
                  <div className={styles.rowFoot}>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => openDetail(s)}
                    >
                      Detaljer
                    </Button>
                    <PendingButtons s={s} />
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
        <Pagination
          className={styles.pagination}
          page={page}
          pageCount={data?.totalPages ?? 0}
          onChange={(p) => {
            setPage(p);
            window.scrollTo?.({ top: 0 });
          }}
        />
      </Card>

      <PageModal
        open={!!detail}
        title={
          detail
            ? `${detail.extractedName || "Opplasting"} (#${detail.id})`
            : ""
        }
        icon="moon"
        width={1000}
        onClose={() => setDetail(null)}
        footer={
          <Button variant="secondary" onClick={() => setDetail(null)}>
            Lukk
          </Button>
        }
      >
        {detail && (
          <div className={styles.detail}>
            <div className={styles.detailImage}>
              {detail.imageUrl ? (
                <a href={detail.imageUrl} target="_blank" rel="noreferrer">
                  <img src={detail.imageUrl} alt="Den opplastede plakaten" />
                </a>
              ) : (
                <span className={ui.help}>Ikke noe bilde lagret.</span>
              )}
            </div>
            <div className={styles.detailInfo}>
              <div className={styles.head}>
                <Badge tone={resultTone(detail.result)}>
                  {resultLabel(detail.result)}
                </Badge>
                {detail.pushSent && <Badge tone="mint">Push sendt</Badge>}
              </div>
              <dl className={styles.fields}>
                {(
                  [
                    ["Tidspunkt", formatLogTime(detail.createdAt)],
                    ["Grunn", detail.reason],
                    ["Sikkerhet", formatConfidence(detail.confidence)],
                    ["Byrå", detail.agencyName || detail.agencyKey],
                    [
                      "Annonse",
                      detail.noticeId != null
                        ? `#${detail.noticeId}${detail.noticeName ? ` ${detail.noticeName}` : " (slettet)"}`
                        : null,
                    ],
                    ["Status nå", detail.noticeStatus],
                    [
                      "Plattform",
                      [detail.platform, detail.appVersion]
                        .filter(Boolean)
                        .join(" "),
                    ],
                    ["Lastet opp av", detail.submittedBy],
                    ["Modell", detail.aiModel],
                    ["Tid brukt", seconds(detail.durationMs)],
                  ] as Array<[string, string | null]>
                )
                  .concat(aiFields(detail.aiJson))
                  .filter(([, v]) => !!v)
                  .map(([label, value], i) => (
                    <React.Fragment key={`${label}-${i}`}>
                      <dt>{label}</dt>
                      <dd>{value}</dd>
                    </React.Fragment>
                  ))}
              </dl>
              <PendingButtons s={detail} />
              <h3 className={styles.jsonTitle}>AI-svar (JSON)</h3>
              {detailLoading ? (
                <Skeleton height={160} />
              ) : detail.aiJson ? (
                <pre className={styles.json}>{prettyJson(detail.aiJson)}</pre>
              ) : (
                <span className={ui.help}>
                  AI-en ble ikke spurt (eller svarte ikke).
                </span>
              )}
            </div>
          </div>
        )}
      </PageModal>
    </>
  );
};

export default JanazahAiLogPage;
